import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs';
import { UnlinkedResultDto } from 'src/app/core/models/unlinked-result.model';
import { FeedbackService } from 'src/app/core/services/feedback.service';
import { PatientService } from 'src/app/core/services/patient.service';
import {
  UnlinkedCandidateMatchService,
  UnlinkedCandidateRow
} from 'src/app/core/services/unlinked-candidate-match.service';
import { toUnlinkedMatchSubject } from 'src/app/core/unlinked-match/unlinked-candidate-match-core';
import { PatientSelectionDialogComponent } from '../patient-selection-dialog/patient-selection-dialog.component';

export interface UnlinkedResultLinkDialogData {
  unlinked: UnlinkedResultDto;
}

export interface UnlinkedResultLinkDialogResult {
  patientId: string;
  patientLabel: string;
  /** How the human found this patient — recorded for the confirmation copy only. */
  via: 'exact-identity' | 'fuzzy' | 'search';
}

/**
 * Presents ranked patient candidates for one unlinked pathology result.
 *
 * Nothing here links anything and nothing is pre-selected: the dialog closes with the
 * patient the clinician explicitly clicked, and the caller performs the link after its
 * own confirmation step.
 */
@Component({
  selector: 'app-unlinked-result-link-dialog',
  templateUrl: './unlinked-result-link-dialog.component.html',
  styleUrls: ['./unlinked-result-link-dialog.component.scss']
})
export class UnlinkedResultLinkDialogComponent implements OnInit {
  unlinked: UnlinkedResultDto;

  exactCandidates: UnlinkedCandidateRow[] = [];
  fuzzyCandidates: UnlinkedCandidateRow[] = [];
  ranking = true;

  /** Snapshot of the in-memory patient list, reused for the manual search fallback. */
  private patients: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<UnlinkedResultLinkDialogComponent, UnlinkedResultLinkDialogResult | undefined>,
    private patientService: PatientService,
    private feedbackService: FeedbackService,
    private candidateMatchService: UnlinkedCandidateMatchService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: UnlinkedResultLinkDialogData
  ) {
    this.unlinked = data?.unlinked ?? ({ id: '' } as UnlinkedResultDto);
  }

  ngOnInit(): void {
    this.patientService.patients$
      .pipe(take(1))
      .subscribe(patients => {
        this.patients = patients ?? [];
        const subject = toUnlinkedMatchSubject(this.unlinked?.demographics);
        this.candidateMatchService.rankCandidates(subject, this.patients)
          .then(rows => {
            this.exactCandidates = rows.filter(r => r.kind === 'exact-identity');
            this.fuzzyCandidates = rows.filter(r => r.kind === 'fuzzy');
            this.ranking = false;
          })
          .catch(() => {
            this.exactCandidates = [];
            this.fuzzyCandidates = [];
            this.ranking = false;
          });
      });
  }

  get resultName(): string {
    const d = this.unlinked?.demographics;
    const surname = (d?.surname ?? '').trim();
    const first = (d?.firstName ?? '').trim();
    const joined = [surname, first].filter(Boolean).join(', ');
    return joined || 'Unknown patient';
  }

  patientLabel(candidate: UnlinkedCandidateRow): string {
    return this.formatPatientLabel(candidate.patient);
  }

  private formatPatientLabel(patient: any): string {
    const surname = (patient?.surname ?? '').trim();
    const name = (patient?.name ?? '').trim();
    const number = (patient?.patientNumber ?? '').trim();
    const base = [surname, name].filter(Boolean).join(', ') || 'Unnamed patient';
    return number ? `${base} (${number})` : base;
  }

  /** An explicit click on one candidate. Still only a proposal — the caller confirms. */
  chooseCandidate(candidate: UnlinkedCandidateRow): void {
    const patientId = candidate?.patient?.id;
    if (!patientId) {
      return;
    }
    this.dialogRef.close({
      patientId,
      patientLabel: this.patientLabel(candidate),
      via: candidate.kind === 'exact-identity' ? 'exact-identity' : 'fuzzy'
    });
  }

  /** Fallback when the ranking has nothing useful: search the whole in-memory patient list. */
  searchAllPatients(): void {
    const searchRef = this.dialog.open(PatientSelectionDialogComponent, {
      width: '400px',
      data: { patients: this.patients }
    });

    searchRef.afterClosed().subscribe((selected: any) => {
      // Polymorphic close value: a patient object, the sentinels 'invite' / 'event', or undefined.
      if (!selected) {
        return;
      }
      if (typeof selected === 'string') {
        // The dialog's close value is polymorphic: 'invite' / 'event' / 'opd' mean the user
        // asked to create something new, which is not a way to resolve an inbound result.
        this.feedbackService.showToast(
          'Create the patient from the patient list first, then link this result.', 'OK', 4000
        );
        return;
      }
      if (!selected.id) {
        return;
      }
      this.dialogRef.close({
        patientId: selected.id,
        patientLabel: this.formatPatientLabel(selected),
        via: 'search'
      });
    });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
